import { useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import { TrendingUp, TrendingDown, Receipt, ArrowUpRight, Store, Coins } from "lucide-react";
import StatCard from "../components/StatCard";
import {
  TEAMS, CUSTOMERS, summaryForFilter, teamSummaries,
  dailySeries, bayiReport, formatTRY, formatInt,
} from "../data/mock";

const compact = (n) => new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(n);

export default function Dashboard() {
  const s = useMemo(() => summaryForFilter({}), []);
  const teams = useMemo(() => teamSummaries({}), []);
  const daily = useMemo(() => dailySeries({}), []);
  const bayiler = useMemo(() => bayiReport({}).sort((a, b) => b.yatirim - a.yatirim).slice(0, 8), []);
  const sortedTeams = [...teams].sort((a, b) => b.yatirim - a.yatirim);
  const topT = sortedTeams.slice(0, 6);
  const restT = sortedTeams.slice(6);
  const pieData = [
    ...topT.map((t) => ({ name: t.name, value: Math.round(t.yatirim), color: t.color })),
    ...(restT.length ? [{ name: "Diğer", value: Math.round(restT.reduce((a, t) => a + t.yatirim, 0)), color: "#CBD5E1" }] : []),
  ];

  return (
    <div className="space-y-6" data-testid="page-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Genel Bakış</h1>
        <p className="text-slate-500 mt-1 text-sm">Günlük ortalama · 10.07.2026 – 16.07.2026 · {TEAMS.length} bayi</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Günlük Yatırım" value={formatTRY(s.yatirim / 7)} color="#78C043" icon={TrendingUp} testid="dash-yatirim" />
        <StatCard title="Günlük Çekim" value={formatTRY(s.cekim / 7)} color="#F26063" icon={TrendingDown} testid="dash-cekim" />
        <StatCard title="Günlük İşlem" value={formatInt(s.adet / 7)} color="#F0813E" icon={Receipt} testid="dash-islem" />
        <StatCard title="Günlük Komisyon" value={formatTRY(s.komisyon / 7)} color="#7A2FBE" icon={Coins} testid="dash-komisyon" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white dark:bg-[#151824] rounded-2xl p-5 shadow-sm border border-slate-100 dark:border-slate-800">
          <h3 className="font-bold mb-4">Günlük Yatırım / Çekim</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={daily} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="gun" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={compact} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => formatTRY(v)} contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }} />
              <Bar dataKey="Yatırım" fill="#78C043" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Çekim" fill="#F26063" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-[#151824] rounded-2xl p-5 shadow-sm border border-slate-100 dark:border-slate-800">
          <h3 className="font-bold mb-4">Bayi Bazlı Yatırım</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {pieData.map((e, i) => (<Cell key={i} fill={e.color} />))}
              </Pie>
              <Tooltip formatter={(v) => formatTRY(v)} contentStyle={{ borderRadius: 12, border: "none" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {pieData.map((e) => (
              <div key={e.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: e.color }} />{e.name}</span>
                <span className="font-semibold">{formatTRY(e.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-[#151824] rounded-2xl p-5 shadow-sm border border-slate-100 dark:border-slate-800">
          <h3 className="font-bold mb-4">İşlem Hacmi Trendi</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={daily}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F0651E" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#F0651E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="gun" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => `${formatInt(v)} işlem`} contentStyle={{ borderRadius: 12, border: "none" }} />
              <Area type="monotone" dataKey="islem" stroke="#F0651E" strokeWidth={2.5} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-[#151824] rounded-2xl p-5 shadow-sm border border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">Bayi Performansı</h3>
            <span className="text-xs text-slate-400 flex items-center gap-1"><Store size={14} /> {TEAMS.length} bayi · {CUSTOMERS.length} kullanıcı</span>
          </div>
          <div className="space-y-2">
            {bayiler.map((b, i) => (
              <div key={b.id} className="flex items-center gap-3 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
                <span className="w-9 h-9 rounded-xl text-white flex items-center justify-center text-xs font-bold" style={{ background: b.color }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{b.name}</div>
                  <div className="text-xs text-slate-500 truncate">Min. {b.minLabel} · {formatInt(b.islemAdedi)} işlem</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-sm text-green-600 flex items-center gap-1 justify-end"><ArrowUpRight size={14} />{formatTRY(b.yatirim)}</div>
                  <div className="text-xs text-slate-400">~{formatInt(b.islemBasi1M)} işlem/1M</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
